import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { isArray } from 'util';
import { environment } from '../../../../environments/environment';
import { CamProgramArticleFormInterface } from '../../../dashboard/cam/cam-program-article/cam-program-article-form.interface';
// tslint:disable:max-line-length
import { CamProgramArticleFormModelProgramItemInterface } from '../../../dashboard/cam/cam-program-article/cam-program-article-form/cam-program-article-form-model-program-item.interface';
import { SchedulerTaskCreatingInterface } from '../../../dashboard/scheduler/scheduler-dashboard/scheduler-dashboard-by-part/scheduler-task-creating.interface';
import { NewAndUpdatedSchedulerTaskManufacturingOrderBuilder } from '../../models/builders/new-and-updated-scheduler-task-manufacturing-order.builder';
// tslint:enable:max-line-length
import { SchedulerTaskManufacturingOrderBuilder } from '../../models/builders/scheduler-task-manufacturing-order.builder';
import { SchedulerTaskOrderFieldBuilder } from '../../models/builders/scheduler-task-order-field.builder';
import { SchedulerTaskWithModelTimeLineSimpleBuilder } from '../../models/builders/scheduler-task-with-model-timeline-simple.builder';
import { SchedulerTaskBuilder } from '../../models/builders/scheduler-task.builder';
import { totalInfoBuilder } from '../../models/builders/total-info.builder';
import { mapCollection } from '../../utils/collection.utils';
import { getMillis } from '../../utils/date.utils';
import { valueOf } from '../../utils/string.utils';
import { of } from 'rxjs/observable/of';
import { forkJoin } from 'rxjs/observable/forkJoin';

const createAddToPlanningBody = (body: SchedulerTaskCreatingInterface) => ({
  manufacturingOrderHash: body.manufacturingOrderHash,
  modelHash: body.modelHash,
  date: getMillis(body.date),
  operations: body.operations,
});

const createAddLastToPlanningBody = (body: SchedulerTaskCreatingInterface) => ({
  manufacturingOrderHash: body.manufacturingOrderHash,
  modelHash: body.modelHash,
  operations: body.operations,
});

const createStatusBody = (schedulerTaskHash: string, statusCode: number) => ({
  schedulerTaskHash: schedulerTaskHash,
  status: statusCode,
});

const sortBody = (data: { fieldCode: number; order: string }) => {
  return {
    sortBy: data.fieldCode,
    sortDir: data.order,
  };
};

const createOrderTasksBody = (
  data: { fieldCode: number; order: string }[]
): any => ({
  orderFields: mapCollection(sortBody, data),
});

const getUpdateCamProgramArticleFormModelProgramBody = (
  modelsPrograms: CamProgramArticleFormModelProgramItemInterface[]
) => {
  return isArray(modelsPrograms)
    ? modelsPrograms.map((modelProgram) => ({
        modelHash: modelProgram.modelHash,
        programReference: modelProgram.programReference,
      }))
    : [];
};

const getFindNotFinishedPlannedSchedulerTaskByModelBody = (
  cam: CamProgramArticleFormInterface
) => {
  const getBody = new FormData();
  getBody.append('articleHash', cam.articleHash);
  getBody.append('operationHash', cam.operationHash);
  getBody.append(
    'modelsPrograms',
    JSON.stringify(
      getUpdateCamProgramArticleFormModelProgramBody(cam.modelsPrograms)
    )
  );

  return getBody;
};

const getEditSchedulerTaskToolInfoBody = (
  toolLocation: string,
  toolDeliveryDate: number
) => ({
  toolLocation: toolLocation,
  toolDeliveryDate: toolDeliveryDate,
});

@Injectable()
export class SchedulerTaskByPartApiService {
  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}scheduler-tasks`;
  }

  moveSchedulerTask(originHash: string, destinationHash: string) {
    return this.httpClient
      .put(`${this.apiUrl}/${originHash}/move/${destinationHash}`, {})
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromList));
  }

  getSchedulerTaskPaginated(model: string, count: number) {
    const params = new HttpParams()
      .append('model', model)
      .append('count', valueOf(count));

    return this.httpClient
      .get(`${this.apiUrl}/paginated-closest-scheduler-task`, { params })
      .pipe(
        map(totalInfoBuilder(SchedulerTaskManufacturingOrderBuilder.fromList))
      );
  }

  getSchedulerTaskPaginatedNexts(model: string, from: string, count: number) {
    const params = new HttpParams()
      .append('model', model)
      .append('startSchedulerTaskHash', from)
      .append('count', valueOf(count));

    return this.httpClient
      .get(`${this.apiUrl}/paginated-down-scheduler-tasks`, { params })
      .pipe(
        map(totalInfoBuilder(SchedulerTaskManufacturingOrderBuilder.fromList))
      );
  }

  getSchedulerTaskPaginatedPrevs(model: string, from: string, count: number) {
    const params = new HttpParams()
      .append('model', model)
      .append('startSchedulerTaskHash', from)
      .append('count', valueOf(count));

    return this.httpClient
      .get(`${this.apiUrl}/paginated-up-scheduler-tasks`, { params })
      .pipe(
        map(
          totalInfoBuilder(
            SchedulerTaskManufacturingOrderBuilder.fromListReverse
          )
        )
      );
  }

  getSchedulerTaskByModelAndBetweenDates(
    model: string,
    startDate: number,
    endDate: number
  ) {
    const params = new HttpParams()
      .append('model', model)
      .append('start', valueOf(startDate))
      .append('end', valueOf(endDate));

    return this.httpClient
      .get(`${this.apiUrl}`, { params })
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromList));
  }

  addToPlanning(params) {
    return this.httpClient
      .post(`${this.apiUrl}/add-to-planning`, createAddToPlanningBody(params))
      .pipe(map(NewAndUpdatedSchedulerTaskManufacturingOrderBuilder.fromJson));
  }

  addLastToPlanning(params) {
    return this.httpClient
      .post(
        `${this.apiUrl}/add-last-to-planning`,
        createAddLastToPlanningBody(params)
      )
      .pipe(map(NewAndUpdatedSchedulerTaskManufacturingOrderBuilder.fromJson));
  }

  deleteTask(task: string) {
    return this.httpClient
      .delete(`${this.apiUrl}/${task}`)
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromList));
  }

  getSchedulerTasks(start: number, end: number) {
    const params = new HttpParams()
      .append('start', valueOf(start))
      .append('end', valueOf(end));

    return this.httpClient
      .get(`${this.apiUrl}/scheduler`, { params })
      .pipe(map(SchedulerTaskBuilder.fromList));
  }

  getSchedulerTasksBetweenDatesAndModels(
    start: number,
    end: number,
    models: any
  ) {
    if (!models || models.length === 0) {
      return of([]);
    }

    const requests = models.map((model: any) => {
      const params = new HttpParams()
        .append('start', valueOf(start))
        .append('end', valueOf(end))
        .append('models', model.hash);

      return this.httpClient.get(`${this.apiUrl}/scheduler`, { params }).pipe(
        map(SchedulerTaskWithModelTimeLineSimpleBuilder.fromList),
        catchError((error) => {
          console.error(`Error fetching tasks for model ${model.hash}:`, error);
          return of([]);
        })
      );
    });

    return forkJoin(requests).pipe(
      map((results: any[][]) =>
        results.reduce((acc, val) => acc.concat(val), [])
      )
    );
  }

  sendSchedulerTaskToProduction(
    hash: string,
    finished: boolean,
    userErpCode: string
  ) {
    return this.httpClient
      .post(`${this.apiUrl}/${hash}/sendToProduction`, {
        finished: finished,
        userErpCode: userErpCode,
      })
      .pipe(map(SchedulerTaskBuilder.fromJson));
  }

  changeSchedulerTaskStatus(schedulerTaskHash: string, statusCode: number) {
    return this.httpClient
      .post(
        `${this.apiUrl}/scheduler-task-status`,
        createStatusBody(schedulerTaskHash, statusCode)
      )
      .pipe(map(SchedulerTaskBuilder.fromJson));
  }

  getSchedulerTaskByModel(hash: string) {
    const params = new HttpParams().append('model', hash);
    return this.httpClient
      .get(`${this.apiUrl}/to-send`, { params })
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromList));
  }
  isSendableSchedulerTask(schedulerTaskHash: string) {
    return this.httpClient
      .get<{ sendableStatus: number }>(
        `${this.apiUrl}/${schedulerTaskHash}/is-sendable`
      )
      .pipe(map((data) => data.sendableStatus));
  }

  changeTaskBlockStatus(hash: string, blocked: boolean) {
    return this.httpClient
      .post(`${this.apiUrl}/${hash}/block`, { blocked })
      .pipe(map(SchedulerTaskBuilder.fromJson));
  }

  getSchedulerTaskOrderFields() {
    return this.httpClient
      .get(`${this.apiUrl}/order-fields`)
      .pipe(map(SchedulerTaskOrderFieldBuilder.fromList));
  }

  orderTasks(data: { fieldCode: number; order: string }[], hash: string) {
    return this.httpClient
      .put(`${this.apiUrl}/${hash}/order-tasks`, createOrderTasksBody(data))
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromList));
  }

  getPlannedNotFinishedSchedulerTaskByModel(
    cam: CamProgramArticleFormInterface
  ) {
    return this.httpClient
      .post(
        `${this.apiUrl}/planned-with-same-program`,
        getFindNotFinishedPlannedSchedulerTaskByModelBody(cam)
      )
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromList));
  }

  getSchedulerTask(hash: string) {
    return this.httpClient
      .get(`${this.apiUrl}/${hash}`)
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromJson));
  }

  editSchedulerTaskToolInfo(
    hash: string,
    toolLocation: string,
    toolDeliveryDate: number
  ) {
    return this.httpClient
      .put(
        `${this.apiUrl}/${hash}/tool-info`,
        getEditSchedulerTaskToolInfoBody(toolLocation, toolDeliveryDate)
      )
      .pipe(map(SchedulerTaskManufacturingOrderBuilder.fromJson));
  }

  getToolTableFromSchedulerTask(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/table-tool`, {
      responseType: 'blob',
    });
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/scheduler-task-by-part-api.service.ts